import { createFileRoute } from "@tanstack/react-router";
import {
  Content,
  fetchEntries,
  fetchOneEntry,
  getBuilderSearchParams,
} from "@builder.io/sdk-react/edge";
import { clientEnv } from "~/lib/utils";
import { CUSTOM_COMPONENTS } from "~/components/builder-registry";
import { BlogGrid } from "~/components/blog-grid";
import {
  createArticleMeta,
  createArticleStructuredData,
} from "~/lib/shared/meta";

export const Route = createFileRoute("/blog/$slug/")({
  loaderDeps: ({ search }) => {
    return {
      search,
    };
  },
  loader: async ({ params, deps }) => {
    const builderContent = await fetchOneEntry({
      model: "blog-article",
      apiKey: clientEnv.VITE_BUILDER_API_KEY,
      query: {
        "data.slug": params.slug,
      },
      includeUnpublished: false,
      userAttributes: {
        urlPath: `/blog/${params.slug}`,
      },
      options: getBuilderSearchParams(deps.search),
    });

    if (!builderContent) {
      throw new Response("Page not found", { status: 404 });
    }

    return {
      content: builderContent,
    };
  },
  head: ({ loaderData }) => {
    const { content } = loaderData ?? {};
    const article = content?.data;

    if (!article) {
      return {
        title: "Blog Post - Dream Coach Match",
        meta: [
          {
            name: "description",
            content: "Read our latest blog post on Dream Coach Match",
          },
        ],
      };
    }

    const authorName =
      "string" === typeof article.author
        ? article.author
        : article.author?.value?.data?.fullName ||
          article.author?.value?.name ||
          "Dream Coach Match";

    const publishedDate = article.publishedDate
      ? new Date(article.publishedDate).toISOString()
      : undefined;
    const url = `https://dreamcoachmatch.com/blog/${content?.data?.slug}`;

    // Create a more compelling meta description
    const createMetaDescription = () => {
      if (article.description) return article.description;
      if (article.excerpt) return article.excerpt;

      // Fallback: create description from title and author
      const baseDescription = `Discover insights from ${authorName} on Dream Coach Match.`;
      if (article.title) {
        return `${article.title} - ${baseDescription}`;
      }
      return baseDescription;
    };

    const metaTags = createArticleMeta(article.title || "Blog Post", {
      description: createMetaDescription(),
      ogImage: article.image,
      ogUrl: url,
      canonical: url,
      author: authorName,
      publishedTime: publishedDate,
      tags: article.tags,
      keywords: article.keywords || article.tags,
    });

    // Add structured data
    const structuredData = createArticleStructuredData({
      headline: article.title || "Blog Post",
      description: article.description || article.excerpt,
      image: article.image,
      author: authorName,
      publishedDate: publishedDate,
      url: url,
    });

    // Add breadcrumb structured data
    const breadcrumbStructuredData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://dreamcoachmatch.com",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: "https://dreamcoachmatch.com/blog",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title || "Blog Post",
          item: url,
        },
      ],
    };

    return {
      ...metaTags,
      scripts: [
        {
          type: "application/ld+json",
          innerHTML: JSON.stringify(structuredData),
        },
        {
          type: "application/ld+json",
          innerHTML: JSON.stringify(breadcrumbStructuredData),
        },
      ],
    };
  },
  component: BlogArticlePage,
});

function BlogArticlePage() {
  const { content } = Route.useLoaderData();

  return (
    <article
      className="mx-auto min-h-screen px-4 py-16 sm:px-6 sm:py-24 lg:px-8"
      itemScope
      itemType="https://schema.org/Article"
    >
      <header className="mx-auto max-w-prose text-center">
        {content.data?.title && (
          <h1
            className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl"
            itemProp="headline"
          >
            {content.data.title}
          </h1>
        )}
        {(content.data?.author || content.data?.date) && (
          <div className="mt-6 text-lg leading-8 text-gray-600">
            {content.data?.author && (
              <span
                itemProp="author"
                itemScope
                itemType="https://schema.org/Person"
              >
                By{" "}
                <span itemProp="name">
                  {"string" === typeof content.data.author
                    ? content.data.author
                    : content.data.author.value?.data?.fullName ||
                      content.data.author.value?.name ||
                      "Unknown Author"}
                </span>
              </span>
            )}
            {content.data?.date && (
              <time
                className="ml-2 text-gray-400"
                dateTime={new Date(content.data.date).toISOString()}
                itemProp="datePublished"
              >
                •{" "}
                {new Date(content.data.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            )}
          </div>
        )}
        {content.data?.image && (
          <div className="mt-10">
            <img
              src={content.data.image}
              alt={content.data.title || "Blog post image"}
              className="mx-auto max-w-full rounded-lg shadow-lg"
              itemProp="image"
            />
          </div>
        )}
      </header>

      <main className="mt-16">
        <Content
          model="blog-article"
          content={content}
          apiKey={clientEnv.VITE_BUILDER_API_KEY}
          customComponents={CUSTOM_COMPONENTS}
        />
      </main>

      <div className="mx-auto max-w-prose">
        <BlogGrid />
      </div>
    </article>
  );
}

export const generateStaticParams = async () => {
  // Fetch all blog articles, just the slug and date
  const articles = await fetchEntries({
    model: "blog-article",
    apiKey: clientEnv.VITE_BUILDER_API_KEY,
    omit: "data.blocks",
  });

  return articles
    .filter((a) => !!a.data)
    .map((article) => ({
      slug: article.data!.slug,
    }));
};
